import { FC, useCallback, useEffect, useState } from 'react'

import { useSetAtom } from 'jotai'

import {
  Text,
  GreyGrey80,
  Primary13Regular,
  RedRed60,
  SequenceMenu,
  useNotifications,
  SimpleModal
} from '@sequencehq/core-components'
import { useSequenceInfiniteQuery } from '@sequencehq/api'
import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'

import {
  editingVersionAtom,
  lastSavedPresentationAtom,
  presentationAtom,
  resetPresentationHistoryAtom,
  versionPreviewAtom
} from 'Automations/automationEditor/atoms'
import { parseVersionNumber } from 'Automations/helpers'
import { isAutomationPresentation } from 'Automations/nodeDefinitions/guards'

type AutomationVersion = Dashboardv99990101Api.Automations.AutomationVersion

export const DiscardUnpublishedChangesModal: FC<{
  automationId: string
  activeVersion: AutomationVersion
  onSuccess?: () => void
  onClose: () => void
}> = ({ automationId, activeVersion, onSuccess, onClose }) => {
  const { displayNotification } = useNotifications()
  const [isDiscarding, setIsDiscarding] = useState(false)

  const setPresentation = useSetAtom(presentationAtom)
  const setLastSavedPresentation = useSetAtom(lastSavedPresentationAtom)
  const setEditingVersion = useSetAtom(editingVersionAtom)
  const setVersionPreview = useSetAtom(versionPreviewAtom)
  const resetHistory = useSetAtom(resetPresentationHistoryAtom)

  const { infiniteQuery } = useSequenceInfiniteQuery(
    dashboardv99990101Client.getAutomationVersions,
    { automationId }
  )

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    infiniteQuery

  // Eagerly load every page so all versions newer than the published one are
  // archived, not just those on the first page.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const isLoadingVersions = isLoading || hasNextPage

  const discardUnpublishedChanges = useCallback(async () => {
    const publishedVersionNumber = parseVersionNumber(
      activeVersion.versionNumber
    )

    // Non-archived versions numbered above the published one are the
    // unpublished changes to discard.
    const versionsToArchive = (data?.pages ?? [])
      .flatMap(page => page.items)
      .filter(
        version =>
          !version.archivedAt &&
          parseVersionNumber(version.versionNumber) > publishedVersionNumber
      )

    const archiveResults = await Promise.all(
      versionsToArchive.map(version =>
        dashboardv99990101Client.postArchiveAutomationVersion({
          automationId,
          versionId: version.id
        })
      )
    )

    const archivedAll = archiveResults.every(result => !result.error)

    if (!archivedAll || !isAutomationPresentation(activeVersion.presentation)) {
      displayNotification('Failed to discard unpublished changes', {
        type: 'error'
      })
      return false
    }

    // Revert the editor to the published version, mirroring the restore flow.
    setLastSavedPresentation(activeVersion.presentation)
    setPresentation(activeVersion.presentation)
    setEditingVersion(activeVersion)
    setVersionPreview(undefined)
    resetHistory()

    displayNotification('Unpublished changes discarded', { type: 'success' })
    onSuccess?.()

    return true
  }, [
    automationId,
    activeVersion,
    data?.pages,
    displayNotification,
    onSuccess,
    setLastSavedPresentation,
    setPresentation,
    setEditingVersion,
    setVersionPreview,
    resetHistory
  ])

  return (
    <SimpleModal
      isLoading={isLoadingVersions}
      isOpen
      onClose={onClose}
      variant="dialog"
      width="436px"
      title="Discard unpublished changes"
      data-testid="automations.editor.discardUnpublishedChangesModal"
      submitButtonText="Discard changes"
      submittingButtonText="Discarding..."
      submitting={isDiscarding}
      isDestructive
      onSubmit={async () => {
        setIsDiscarding(true)
        const result = await discardUnpublishedChanges()
        setIsDiscarding(false)
        return result
      }}
    >
      <Text {...Primary13Regular} color={GreyGrey80}>
        Are you sure you want to discard your unpublished changes? The workflow
        will revert to the published version and any newer versions will be
        archived.
      </Text>
    </SimpleModal>
  )
}

export const DiscardUnpublishedChangesMenuItem: FC<{
  automationId: string
  activeVersion: AutomationVersion
  onSuccess?: () => void
}> = ({ automationId, activeVersion, onSuccess }) => {
  const [showModal, setShowModal] = useState(false)

  return (
    <>
      <SequenceMenu.Item
        key="discard-unpublished-changes"
        uuid="discard-unpublished-changes"
        label="Discard unpublished changes"
        data-testid="automations.editor.kebabMenu.discardUnpublishedChanges"
        style={{ color: RedRed60 }}
        onClick={() => setShowModal(true)}
      />
      {showModal && (
        <DiscardUnpublishedChangesModal
          automationId={automationId}
          activeVersion={activeVersion}
          onSuccess={onSuccess}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  )
}
